import { useChatStore, ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { TModelType } from "@/zapi-frontend/shared/types/models";
import { safeJsonParse } from "@/zapi-frontend/utils/safeJsonParse";
import $api from "@/zapi-frontend/shared/api/axios";
import { useState } from "react";
import { MAX_CHAT_HISTORY_LENGTH } from "@/zapi-frontend/shared/const/consts";
import { removeContentFromResponse } from "../lib/removeContentFromResponse";
import { getErrorConfig } from "@/zapi-frontend/shared/config/errorMessages";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

function getLastItems<T>(arr: T[], n: number): T[] {
  return arr.slice(-n);
}

const cleanupMessages = (messages: ChatMessageType[]) => {
  return messages
    .map((message) => {
      if (message.role === "user") return message;
      try {
        const parsedMessage = safeJsonParse(message.content);
        if (parsedMessage.type === "updateChapter") {
          return {
            ...message,
            content: removeContentFromResponse(message.content)
          };
        }
        return message;
      } catch {
        return message;
      }
    })
    .filter((m) => !!m) as ChatMessageType[];
};

export function useChatGenerate() {
  const { getChatHistory, setLoadingState, resetLoadingState } = useChatStore();
  const { isLabs } = useMeStore();
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const startGeneration = async (
    projectId: string,
    chapterId: string,
    content: string,
    model: TModelType | null
  ): Promise<number | null> => {
    const botMessageId = `bot-${Date.now()}`;
    const botLoadingMessage: ChatMessageType = {
      role: "assistant",
      content: "",
      id: botMessageId
    };

    try {
      setIsSendingRequest(true);

      const response = await $api.post<{ generationId: number }>("/chat/generate", {
        projectId: Number(projectId),
        content: content || "",
        messagesHistory: [
          ...getLastItems(cleanupMessages(getChatHistory(projectId, chapterId)), MAX_CHAT_HISTORY_LENGTH)
        ],
        chapterId: Number(chapterId),
        model: model
      });

      setLoadingState(projectId, chapterId, {
        loadingMessageId: botMessageId,
        pendingBotMessage: botLoadingMessage,
        pendingGenerationId: response.data.generationId,
        isGeneratingChapter: false,
        prereqsChaptersToBeGenerated: null
      });

      return response.data.generationId;
    } catch (error) {
      console.error("Error starting generation:", error);

      let errorMessage = "Unknown error";
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as { response?: { data?: { error?: string } } };
        errorMessage = axiosError.response?.data?.error || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      const errorConfig = getErrorConfig(errorMessage, !!isLabs);

      const errorContent = JSON.stringify({
        type: "error",
        content: errorConfig.userMessage
      });

      setLoadingState(projectId, chapterId, {
        pendingBotMessage: {
          role: "assistant",
          content: errorContent,
          id: botMessageId
        }
      });

      resetLoadingState(projectId, chapterId);
      return null;
    } finally {
      setIsSendingRequest(false);
    }
  };

  return {
    isSendingRequest,
    startGeneration
  };
}
